import { NavLink } from 'react-router';
import {
  LayoutDashboard,
  FileText,
  HelpCircle,
  Inbox,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { useApprovalQueueCount } from '@/hooks/use-workflow';
import { REVIEW_QUEUE_STATUSES } from '@/types/workflow';

interface NavItem {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  badgeKey?: 'approvals';
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/templates', icon: FileText, label: 'Templates' },
  { to: '/approvals', icon: Inbox, label: 'Approvals', badgeKey: 'approvals' },
  { to: '/questions', icon: HelpCircle, label: 'Content Bank' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

// Nav items hidden for the external Health Plan reviewer — they get a reduced
// portal (Dashboard, Templates read-only, Approvals). This is UX only; the
// backend RBAC (canManageQuestion / config handlers) is the real block.
const HEALTH_PLAN_HIDDEN_PATHS = new Set(['/questions', '/settings']);

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { user } = useAuth();
  // CMS-03a: 30s-polled count of templates awaiting this role's review.
  // Hook is no-op (returns 0) when the role has no queue.
  const { count: approvalsCount } = useApprovalQueueCount(user?.role);
  const showApprovalsItem =
    !!user && (REVIEW_QUEUE_STATUSES[user.role]?.length ?? 0) > 0;
  const isHealthPlan = user?.role === 'Health Plan';

  return (
    <aside
      className={cn(
        'flex flex-col border-r border-silver-200 bg-white transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      {/* Brand */}
      <div className="flex h-16 items-center border-b border-silver-200 px-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-ds-008 bg-purple-500">
          <span className="text-sm font-bold text-white">S</span>
        </div>
        {!collapsed && (
          <div className="ml-3 overflow-hidden">
            <p className="text-sm font-semibold text-carbon-900 truncate">CareMonitor</p>
            <p className="text-xs text-carbon-500 truncate">Survey CMS</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-2 py-4">
        {NAV_ITEMS.filter(
          (item) => item.badgeKey !== 'approvals' || showApprovalsItem,
        )
          .filter(
            (item) => !(isHealthPlan && HEALTH_PLAN_HIDDEN_PATHS.has(item.to)),
          )
          .map((item) => {
          const badgeCount =
            item.badgeKey === 'approvals' ? approvalsCount : 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-ds-008 px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-purple-500 text-white'
                    : 'text-carbon-600 hover:bg-purple-50 hover:text-purple-700',
                  collapsed && 'justify-center px-0',
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!collapsed && (
                <span className="flex flex-1 items-center justify-between gap-2">
                  <span className="truncate">{item.label}</span>
                  {badgeCount > 0 && (
                    <span className="rounded-ds-008 bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700">
                      {badgeCount}
                    </span>
                  )}
                </span>
              )}
              {collapsed && badgeCount > 0 && (
                <span className="absolute ml-6 -mt-3 h-2 w-2 rounded-full bg-purple-500" aria-hidden />
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <div className="border-t border-silver-200 p-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center justify-center rounded-ds-008 py-2 text-carbon-400 hover:bg-silver-50 hover:text-carbon-600"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>
    </aside>
  );
}
